import { z } from 'zod';

/**
 * El color es SEMANTICO, nunca implicito por la tecnica (ARQUITECTURA §4).
 * Decir "sRGB porque es DTF" o "serigrafia es CMYK FOGRA39" es falso: la
 * serigrafia trabaja con tintas planas separadas. La conversion NO pertenece al
 * documento: pertenece al `output_profile` del proveedor.
 */
export const RgbColor = z.object({
  type: z.literal('rgb'),
  r: z.number().int().min(0).max(255),
  g: z.number().int().min(0).max(255),
  b: z.number().int().min(0).max(255),
});

export const CmykColor = z.object({
  type: z.literal('cmyk'),
  c: z.number().min(0).max(100),
  m: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
  k: z.number().min(0).max(100),
});

/** Tinta plana con referencia de catalogo (serigrafia, bordado). */
export const SpotColor = z.object({
  type: z.literal('spot'),
  book: z.string().min(1),
  code: z.string().min(1),
  /** Solo para pintar en pantalla. NO es el color de produccion. */
  preview: z.object({
    r: z.number().int().min(0).max(255),
    g: z.number().int().min(0).max(255),
    b: z.number().int().min(0).max(255),
  }),
});

/** Blanco de subbase: es una TINTA SEPARADA, no un color. */
export const TechnicalWhite = z.object({ type: z.literal('technical_white') });

export const Color = z.discriminatedUnion('type', [RgbColor, CmykColor, SpotColor, TechnicalWhite]);
export type Color = z.infer<typeof Color>;
export type RgbColor = z.infer<typeof RgbColor>;
export type SpotColor = z.infer<typeof SpotColor>;

/**
 * Identidad de tinta para contar separaciones. Dos `spot` del mismo libro y
 * codigo son LA MISMA tinta aunque esten en capas distintas; dos `rgb`
 * distintos son cuatricromia, no dos tintas planas.
 */
export function inkKey(color: Color): string {
  switch (color.type) {
    case 'spot':
      return `spot:${color.book}:${color.code}`;
    case 'technical_white':
      return 'technical_white';
    case 'rgb':
      return `rgb:${color.r},${color.g},${color.b}`;
    case 'cmyk':
      return `cmyk:${color.c},${color.m},${color.y},${color.k}`;
  }
}

/** Color aproximado para pintar en pantalla. Nunca para producir. */
export function previewRgb(color: Color): { r: number; g: number; b: number } {
  switch (color.type) {
    case 'rgb':
      return { r: color.r, g: color.g, b: color.b };
    case 'spot':
      return color.preview;
    case 'technical_white':
      return { r: 255, g: 255, b: 255 };
    case 'cmyk': {
      // Conversion NAIVE y solo para pantalla: la real la hace el output_profile.
      const k = color.k / 100;
      const f = (v: number) => Math.round(255 * (1 - v / 100) * (1 - k));
      return { r: f(color.c), g: f(color.m), b: f(color.y) };
    }
  }
}
